import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMentorPersona, getUserName, type MentorPersona } from '../utils/userProfile';
import { isVagueResponse, hasPredominantlyVagueResponses } from '../utils/responseValidation';
import { CHARACTER_LIMITS } from '../config/ui';
import AnimatedText from './onboarding/AnimatedText';
import persistenceService from '../utils/persistenceService';


interface CheckInData {
  response1: string;
  response2: string;
  response3?: string;
  mentorPersona: MentorPersona;
  timestamp: Date;
  completionStatus: 'complete' | 'extended';
}

const QUESTIONS = [
  "What's been weighing on your mind lately, even a little?",
  "What's one small thing you could do for yourself today that no one's asking for?",
  "What's something coming up, even tiny, that you actually feel good about?"
];

const RESPONSE_TEMPLATES = {
  supportive: {
    examples: [
      "That makes complete sense. I can see why that's on your mind. It's wonderful that you're being thoughtful about what you need.",
      "It sounds like you're carrying a lot right now, and it's really meaningful that you're looking ahead to something positive.",
      "I appreciate how honest you're being with yourself. That kind of reflection is what creates real space for change."
    ],
    encouragement: [
      "You're showing real care for your own clarity. That matters.",
      "You're being really thoughtful about this.",
      "That kind of self-awareness is powerful."
    ],
    followUpQuestion: "What's something ahead that you're genuinely excited about, big or small?",
    continueButton: "Keep Reflecting",
    finishButton: "Finish Log"
  },
  firm: {
    examples: [
      "That's clear. Let's put focus where it counts. That's a smart move, and I love that you've claimed something to look forward to.",
      "Good awareness of what's ahead. Here's what matters: you've identified what's on your mind and you're taking action.",
      "That's your leverage point. This is the work. Now let's sharpen it."
    ],
    encouragement: [
      "This is the work. Now let's sharpen it.",
      "Good - you're being direct about what needs attention.",
      "That's the kind of clarity that creates results."
    ],
    followUpQuestion: "What's one thing you're aiming to move forward this week?",
    continueButton: "Push Further",
    finishButton: "Finish Log"
  }
};

const FirstCheckIn: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0); // 0, 1, or 2 for the three questions
  const [responses, setResponses] = useState(['', '', '']); // Three responses for three questions
  const [currentResponse, setCurrentResponse] = useState('');
  const [showMentorResponse, setShowMentorResponse] = useState(false);
  const [showContinueOptions, setShowContinueOptions] = useState(false);
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentorPersona] = useState<MentorPersona>(getMentorPersona());
  const [userName] = useState(getUserName());
  
  // Character limit
  const CHARACTER_LIMIT = CHARACTER_LIMITS.CHECK_IN_RESPONSE;
  const remainingChars = CHARACTER_LIMIT - currentResponse.length;
  
  useEffect(() => {
    // Auto-focus the textarea when component mounts or step changes
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [currentStep]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Silent truncation at 500 characters for system performance
    const truncatedValue = value.slice(0, 500);
    
    // Display limit still enforced at 150 for UI feedback
    if (truncatedValue.length <= CHARACTER_LIMIT) {
      setCurrentResponse(truncatedValue);
    } else {
      setCurrentResponse(truncatedValue.slice(0, CHARACTER_LIMIT));
    }
  };
  
  const handleSubmitResponse = () => {
    if (currentResponse.trim().length === 0) return;
    
    const trimmedResponse = currentResponse.trim();
    const newResponses = [...responses];
    newResponses[currentStep] = trimmedResponse;
    setResponses(newResponses);
    
    // Check if this response is vague and provide immediate guidance
    if (isVagueResponse(trimmedResponse)) {
      // For now, we'll let it continue but could add immediate feedback here
    }
    
    if (currentStep < 2) {
      // Move to next question
      setCurrentStep(currentStep + 1);
      setCurrentResponse('');
    } else {
      // All three questions answered, show mentor response
      setShowMentorResponse(true);
      setShowContinueOptions(false); // Ensure buttons are hidden initially
    }
  };
  

  const mentorResponse = useMemo(() => {
    const template = RESPONSE_TEMPLATES[mentorPersona];
    
    // Check if responses are predominantly vague (at least 2 out of 3 are vague)
    const hasVagueResponses = hasPredominantlyVagueResponses(responses, 2);
    
    if (hasVagueResponses) {
      if (mentorPersona === 'supportive') {
        return "Sometimes 'fine' covers a lot. What's one thing that would make today even better?";
      } else {
        return "Let's get specific. What's actually demanding your attention today?";
      }
    }
    
    const responseTemplate = template.examples[Math.floor(Math.random() * template.examples.length)];
    return responseTemplate;
  }, [mentorPersona, responses]);
  
  const handleContinue = () => {
    setIsFollowUp(true);
    setShowContinueOptions(false);
    setCurrentResponse('');
    
    // Focus textarea for follow-up
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
  };
  
  const handleFinish = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Prepare questions and responses for entry creation
      const questions = [...QUESTIONS];
      const entryResponses = [responses[0], responses[1]];
      
      // Add third response if it exists
      if (responses[2]) {
        questions.push(RESPONSE_TEMPLATES[mentorPersona].followUpQuestion);
        entryResponses.push(responses[2]);
      }
      
      // Create a proper entry in the database
      await persistenceService.createEntryFromCheckin(
        entryResponses, 
        questions, 
        'morning', // First check-in counts as morning check-in
        new Date().toISOString().slice(0, 10) // Today's date
      );
      
      // Save check-in data for backward compatibility
      const checkInData: CheckInData = {
        response1: responses[0],
        response2: responses[1],
        response3: responses[2] || undefined,
        mentorPersona,
        timestamp: new Date(),
        completionStatus: responses[2] ? 'extended' : 'complete'
      };
      localStorage.setItem('firstCheckInComplete', JSON.stringify(checkInData));
      
      // Mark onboarding as complete
      localStorage.setItem('onboardingComplete', 'true');
      
      // Navigate to dashboard only on successful save
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save first check-in:', error);
      
      // Show user-friendly error message
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Unable to save your check-in. Please check your connection and try again.';
      setError(errorMessage);
      setIsSubmitting(false);
      
      // Don't navigate on failure - let user retry
      return;
    }
  };
  

  const handleFollowUpSubmit = async () => {
    // For now, just finish after follow-up. Could expand with more interaction.
    if (!isSubmitting) {
      await handleFinish();
    }
  };
  
  const renderCurrentState = () => {
    if (isFollowUp) {
      return (
        <div className="space-y-6">
          {/* Show previous interaction */}
          <div className="space-y-4 pb-4 border-b border-gray-200">
            <div className="text-sm text-gray-600">
              <strong>ThinkMate:</strong> {QUESTIONS[0]}
            </div>
            <div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg">
              <strong>You:</strong> {responses[0]}
            </div>
            <div className="text-sm text-gray-600">
              <strong>ThinkMate:</strong> {QUESTIONS[1]}
            </div>
            <div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg">
              <strong>You:</strong> {responses[1]}
            </div>
            <div className="text-sm text-gray-600">
              <strong>ThinkMate:</strong> {QUESTIONS[2]}
            </div>
            <div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg">
              <strong>You:</strong> {responses[2]}
            </div>
            <div className="text-sm text-blue-700 bg-blue-50 p-3 rounded-lg">
              <strong>ThinkMate:</strong> {mentorResponse}
            </div>
          </div>
          
          {/* Follow-up section */}
          <div className="space-y-4">
            <div className="text-blue-700 bg-blue-50 p-3 rounded-lg">
              <p className="text-sm font-medium">
                {RESPONSE_TEMPLATES[mentorPersona].encouragement[0]}
              </p>
              <p className="text-sm mt-2">
                {RESPONSE_TEMPLATES[mentorPersona].followUpQuestion}
              </p>
            </div>
            
            <div className="space-y-2">
              <textarea
                ref={textareaRef}
                value={currentResponse}
                onChange={handleInputChange}
                placeholder="Your response..."
                className="w-full p-4 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg text-gray-800"
                rows={4}
                style={{ fontSize: '16px' }} // Prevent zoom on iOS
              />
              <div className="flex justify-between items-center">
                <span className={`text-sm ${
                  remainingChars < 20 ? 'text-red-500' : 'text-gray-500'
                }`}>
                  {remainingChars} characters remaining
                </span>
                <button
                  onClick={handleFollowUpSubmit}
                  disabled={currentResponse.trim().length === 0 || isSubmitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Complete Check-In'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (showMentorResponse) {
      return (
        <div className="space-y-6">
          {/* Show conversation so far */}
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <strong>ThinkMate:</strong> {QUESTIONS[0]}
            </div>
            <div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg">
              <strong>You:</strong> {responses[0]}
            </div>
            <div className="text-sm text-gray-600">
              <strong>ThinkMate:</strong> {QUESTIONS[1]}
            </div>
            <div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg">
              <strong>You:</strong> {responses[1]}
            </div>
            <div className="text-sm text-gray-600">
              <strong>ThinkMate:</strong> {QUESTIONS[2]}
            </div>
            <div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg">
              <strong>You:</strong> {responses[2]}
            </div>
            <div className="text-sm text-blue-700 bg-blue-50 p-3 rounded-lg">
              <strong>ThinkMate:</strong> 
              <AnimatedText
                content={mentorResponse}
                delay={200}
                onComplete={() => setShowContinueOptions(true)}
              />
            </div>
          </div>
          
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <p className="text-sm">{error}</p>
              <button 
                onClick={() => setError(null)}
                className="text-xs text-red-600 hover:text-red-800 mt-1"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Continue options - show after mentor response animation completes */}
          {showContinueOptions && (
            <div className="flex gap-3 justify-center">
                <button
                  onClick={handleContinue}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {RESPONSE_TEMPLATES[mentorPersona].continueButton}
                </button>
                <button
                  onClick={handleFinish}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : RESPONSE_TEMPLATES[mentorPersona].finishButton}
                </button>
              </div>
          )}
        </div>
      );
    }
    
    // Current question state
    return (
      <div className="space-y-6">
        {/* Show previous questions and answers */}
        {currentStep > 0 && (
          <div className="space-y-4 pb-4 border-b border-gray-200">
            {/* Show all previous Q&A pairs */}
            {Array.from({ length: currentStep }).map((_, index) => (
              <div key={index} className="space-y-2">
                <div className="text-sm text-gray-600">
                  <strong>ThinkMate:</strong> {QUESTIONS[index]}
                </div>
                <div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg">
                  <strong>You:</strong> {responses[index]}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Current question */}
        <div className="space-y-4">
          <div className="text-lg text-gray-800 font-medium">
            {QUESTIONS[currentStep]}
          </div>
          
          <div className="space-y-2">
            <textarea
              ref={textareaRef}
              value={currentResponse}
              onChange={handleInputChange}
              placeholder="Your response..."
              className="w-full p-4 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg text-gray-800"
              rows={4}
              style={{ fontSize: '16px' }} // Prevent zoom on iOS
            />
            <div className="flex justify-between items-center">
              <span className={`text-sm ${
                remainingChars < 20 ? 'text-red-500' : 'text-gray-500'
              }`}>
                {remainingChars} characters remaining
              </span>
              <button
                onClick={handleSubmitResponse}
                disabled={currentResponse.trim().length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {currentStep < 2 ? 'Next' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 md:p-8 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="w-full max-w-lg mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold text-gray-800 mb-4">ThinkMate</h1>
          <div className="text-xl text-gray-600 leading-relaxed max-w-md mx-auto space-y-2">
            <p>Hello {userName}!</p>
            <p>Your first check-in.</p>
            <p>This is where momentum begins.</p>
          </div>
        </div>

        {/* Check-in Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10">
          {renderCurrentState()}
        </div>
      </div>
    </div>
  );
};

export default FirstCheckIn; 